import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";

// Routes an incoming Universal Link (iOS) / App Link (Android) - e.g.
// https://iblink.app/e/CODE - into the app's router, both when the app is
// already open (appUrlOpen) and when it's cold-started by the tap (getLaunchUrl).
// Native-only; on web the browser handles the URL directly.
//
// The effect runs ONCE. navigate lives in a ref (its identity changes on every
// route change) and the cold-start launch URL is handled a single time - both
// guard against a navigate loop, since getLaunchUrl() keeps returning the same
// URL for the life of the process.
const DeepLinkHandler = () => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    const go = (url) => {
      try {
        const path = new URL(url).pathname;
        const match = path.match(/\/e\/([A-Za-z0-9]+)/);
        if (match) navigateRef.current(`/e/${match[1].toUpperCase()}`);
      } catch (err) {
        console.error("Deep link parse failed:", err);
      }
    };

    let handle;
    CapApp.addListener("appUrlOpen", (event) => go(event.url)).then((h) => {
      handle = h;
    });
    // Cold start: handle the launch URL exactly once.
    CapApp.getLaunchUrl().then((res) => {
      if (res && res.url) go(res.url);
    });

    return () => {
      if (handle) handle.remove();
    };
    // Run once - navigate is read through the ref above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default DeepLinkHandler;
